import {
    ICustomValidationErrorManagerProxy,
    ICustomValidationFunctionCallContext,
} from "../../../Form/CustomizationContexts/ValidatorContext/ICustomValidationFunctionCallContext";
import { BindingPath, ViewModel } from "../../CoreTypes";
import { IErrorManager } from "../../ErrorManager/ErrorManager";
import { LocalizedString } from "../../Localization/ILocalizer";
import { IModel } from "../../ModelStoring/Model";
import { ModelEvent } from "../../EventManagement/ModelEvents/ModelEvent";

import { CheckType, IValidationContext, ValidationResult, ValidationResults } from "./Bases/IValueValidator";
import { ValueValidatorBase } from "./Bases/ValueValidatorBase";

export type CustomValidationFunction = (
    nodeViewModel: ViewModel,
    nodeAttrs: {},
    path: BindingPath,
    form: ICustomValidationFunctionCallContext
) => string | boolean | undefined | null;

class CustomValidationErrorManagerWithEvents implements ICustomValidationErrorManagerProxy {
    readonly #errorManager: IErrorManager;
    private readonly innerProxy: ICustomValidationErrorManagerProxy;
    private readonly model: IModel;
    private readonly events: ModelEvent[] = [];
    private eventCollectingEnabled = true;
    private readonly targetPath: string;
    private result?: ValidationResult;

    public constructor(
        errorManager: IErrorManager,
        innerProxy: ICustomValidationErrorManagerProxy,
        model: IModel,
        targetPath: string
    ) {
        this.model = model;
        this.#errorManager = errorManager;
        this.innerProxy = innerProxy;
        this.targetPath = targetPath;
    }

    public getEvents(): readonly ModelEvent[] {
        return this.events;
    }

    public getResult(): ValidationResult | undefined {
        return this.result;
    }

    public stopEventCollecting(): void {
        this.eventCollectingEnabled = false;
    }

    public addError(nodePath: string, message: string): void {
        if (this.eventCollectingEnabled) {
            if (this.targetPath === nodePath) {
                this.result = ValidationResults.Invalid(message, "error");
            } else {
                this.#errorManager.addErrorCached(this.model, nodePath, message);
            }
        } else {
            this.innerProxy.addError(nodePath, message);
        }
    }

    public addWarning(nodePath: string, message: string): void {
        if (this.eventCollectingEnabled) {
            if (this.targetPath === nodePath) {
                this.result = ValidationResults.Invalid(message, "warning");
            } else {
                this.#errorManager.addWarningCached(this.model, nodePath, message);
            }
        } else {
            this.innerProxy.addWarning(nodePath, message);
        }
    }

    public clearErrors(nodePath: string): void {
        if (this.eventCollectingEnabled) {
            if (this.targetPath === nodePath) {
                this.result = ValidationResults.Valid;
            } else {
                this.#errorManager.clearErrorsCached(this.model, nodePath);
            }
        } else {
            this.innerProxy.clearErrors(nodePath);
        }
    }

    public clearWarnings(nodePath: string): void {
        if (this.eventCollectingEnabled) {
            if (this.targetPath === nodePath) {
                this.result = ValidationResults.Valid;
            } else {
                this.#errorManager.clearWarningsCached(this.model, nodePath);
            }
        } else {
            this.innerProxy.clearWarnings(nodePath);
        }
    }

    public removeWarning(nodePath: string, message: string): void {
        if (this.eventCollectingEnabled) {
            if (this.targetPath === nodePath) {
                this.result = ValidationResults.Valid;
            } else {
                this.#errorManager.clearWarningsCached(this.model, nodePath);
            }
        } else {
            this.innerProxy.removeWarning(nodePath, message);
        }
    }
}

export class CustomValueValidator extends ValueValidatorBase {
    private readonly customValidationFunction: CustomValidationFunction;
    private readonly message: undefined | LocalizedString;
    private readonly checkType: CheckType;

    public constructor(
        customValidationFunction: CustomValidationFunction,
        checkType: CheckType,
        message: undefined | LocalizedString
    ) {
        super();
        this.message = message;
        this.customValidationFunction = customValidationFunction;
        this.checkType = checkType;
    }

    public validate(context: IValidationContext, nodeViewModel: ViewModel): ValidationResult {
        const callContext = context.customValidationFunctionCallContextProvider.getCustomValidationFunctionCallContext(
            context.model
        );
        const providerWithEvents = new CustomValidationErrorManagerWithEvents(
            context.errorManager,
            callContext.errorManager,
            context.model,
            context.path
        );
        const result = this.customValidationFunction(nodeViewModel, {}, context.path, {
            ...callContext,
            errorManager: providerWithEvents,
        });
        providerWithEvents.stopEventCollecting();
        const additionalEvents = providerWithEvents.getEvents();
        const implicityAppliedResult = providerWithEvents.getResult();
        if (implicityAppliedResult != undefined) {
            return ValidationResults.withEvents(implicityAppliedResult, additionalEvents);
        }
        if (result === false) {
            return ValidationResults.ForceValidEvents(additionalEvents);
        }
        if (result !== true) {
            const message = typeof result === "string" ? result : undefined;
            return ValidationResults.Invalid(
                this.getMessage(context, this.message) ?? message,
                this.checkType,
                additionalEvents
            );
        }
        return ValidationResults.ValidWithEvents(additionalEvents);
    }
}
